import { useEffect, useMemo, useState, type ComponentType } from "react";
import { Select, Pagination, Alert, Form, Input } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import UserAvatar from "../../../../CommonComponents/UserAvatar/UserAvatar";
import "./stylesActivityTab.css";
import CustomDatePicker from "../../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import DataTable from "../../../../shared/dashboards/DataTable/DataTable";
import {
  useGetStaffActivityLog,
  type StaffActivityLogAvailableFilters,
  type StaffActivityLogQueryParams,
  type StaffActivityLogSection,
} from "../../../../../queries/useGetStaffActivityLog";
import { convertToUserTimezone,
  getAdminNow,
} from "../../../../../utils/dateService";
import { NAVIGATION_ROUTES } from "../../../../../utils/NavigationUtils";
import { DATE_RANGE } from "../../../../../utils/Constants";
import { verifyUserId } from "../../../../../utils/verifyUserId";
import {
  AiActionValue,
  AiColumnTitle,
} from "../../../../shared/AiAction/AiAction";

// Section and Action Type use sentinels for the "no filter" state. They are
// never sent to the BE.
const SECTION_ALL = "ALL";
const ACTION_TYPE_ALL = "ALL";

type PanelFilters = {
  section: string;
  action_type: string;
  source_ip: string;
};

const DEFAULT_FILTERS: PanelFilters = {
  section: SECTION_ALL,
  action_type: ACTION_TYPE_ALL,
  source_ip: "",
};

function findSection(
  filters: StaffActivityLogAvailableFilters | null,
  sectionValue: string | undefined,
): StaffActivityLogSection | undefined {
  if (!filters || !sectionValue || sectionValue === SECTION_ALL) return undefined;
  return filters.sections.find((s) => s.value === sectionValue);
}

type ActivityTabProps = {
  staffId?: string | null;
};

type ActivityRow = {
  key: string;
  dateTime: string;
  section: string;
  username: string;
  userId: string;
  actionType: string;
  ipAddress: string;
  amount: number;
  amountType: "approved" | "declined" | "rebet" | null;
  reason: string;
};

type AmountType = ActivityRow["amountType"];

function pickStr(obj: Record<string, unknown>, keys: string[]): string {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && v !== "") return String(v);
  }
  return "";
}

function deriveAmountType(
  actionType: string,
  moneyType: string,
): AmountType {
  const a = (actionType || "").toString();
  if (a.includes("Withdrawal Approved")) return "approved";
  if (a.includes("Withdrawal Declined")) return "declined";
  const m = (moneyType || "").toString().toLowerCase();
  if (
    m === "promo" ||
    m === "rebet" ||
    a.includes("Promo") ||
    a.includes("ReBet")
  ) {
    return "rebet";
  }
  return null;
}

function parseAmount(raw: unknown): number {
  if (raw == null || raw === "") return 0;
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  const n = parseFloat(String(raw).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function mapActivityApiItemToRow(
  item: Record<string, unknown>,
  index: number,
): ActivityRow {
  const o = item && typeof item === "object" ? item : {};
  const actionType = pickStr(o, [
    "action_type",
    "actionType",
    "event_type",
    "type",
  ]);
  const created = pickStr(o, [
    "created_at"
  ]);
  const usernameRaw = pickStr(o, [
    "target_username",
    "targetUsername",
    "username",
    "user_name",
  ]);
  const userId = pickStr(o, [
    "target_user_id",
    "targetUserId",
    "user_id",
    "userId",
  ]);
  const moneyType = pickStr(o, ["money_type", "moneyType", "amount_kind"]);
  const section = pickStr(o, ["event_category", "eventCategory", "section"]);
  const ipAddress = pickStr(o, ["source_ip"]);
  const amount = parseAmount(o.amount ?? o.amount_cents ?? o.value);
  const reason = pickStr(o, [
    "reason",
    "description",
    "notes",
    "message",
    "detail",
  ]);
  const baseKey = pickStr(o, ["event_id", "id", "eventId"]) || "row";
  const key = `${baseKey}-${index}`;

  const dateTime = created
    ? convertToUserTimezone(created)
    : pickStr(o, ["date_time_display", "dateTimeDisplay"]) ||
      pickStr(o, ["date_time", "dateTime"]);

  const username =
    !usernameRaw ? ""
    : usernameRaw.startsWith("@") ? usernameRaw
    : `@${usernameRaw}`;

  return {
    key,
    dateTime,
    section,
    username,
    userId,
    actionType,
    ipAddress,
    amount,
    amountType: deriveAmountType(actionType, moneyType),
    reason,
  };
}

const TypedDataTable = DataTable as unknown as ComponentType<{
  columns: unknown[];
  dataSource: ActivityRow[];
  pagination: false;
  onChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ActivityRow> | SorterResult<ActivityRow>[],
  ) => void;
  rowKey: string;
  scroll: { x: number; y: number };
  loading: boolean;
}>;

function ActivityTab({ staffId }: ActivityTabProps) {
  const [dates, setDates] = useState<[Dayjs, Dayjs]>(() => [
    getAdminNow().subtract(29, "days"),
    getAdminNow(),
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);
  // Filter panel visible by default; toggle from the header collapses the
  // dropdowns + action buttons. Applied filters keep firing the query while
  // the panel is closed, so hiding it never silently changes what's fetched.
  const [showFilters, setShowFilters] = useState(true);

  // Filter form — draft values live in the form; only "applied" values
  // (post-Apply-click) are forwarded to the query hook. Section and Action Type
  // start on the "All" sentinel, which is never sent to the BE.
  const [form] = Form.useForm<PanelFilters>();
  const watchedSection = Form.useWatch("section", form);
  const watchedActionType = Form.useWatch("action_type", form);
  const watchedSourceIp = Form.useWatch("source_ip", form) ?? "";
  const [appliedFilters, setAppliedFilters] =
    useState<PanelFilters>(DEFAULT_FILTERS);

  const logParams = useMemo((): StaffActivityLogQueryParams => {
    const sort_order: StaffActivityLogQueryParams["sort_order"] =
      sortOrder === "ascend" ? "asc"
      : sortOrder === "descend" ? "desc"
      : undefined;
    const wantsSectionFilter = appliedFilters.section !== SECTION_ALL;
    const wantsActionFilter =
      wantsSectionFilter && appliedFilters.action_type !== ACTION_TYPE_ALL;
    return {
      start_date: dates?.[0]?.format?.("YYYY-MM-DD"),
      end_date: dates?.[1]?.format?.("YYYY-MM-DD"),
      page: currentPage,
      page_size: pageSize,
      ...(sortField && { sort_by: sortField, sort_order }),
      ...(wantsSectionFilter && { event_category: appliedFilters.section }),
      ...(wantsActionFilter && { action_type: appliedFilters.action_type }),
      ...(appliedFilters.source_ip && {
        source_ip: appliedFilters.source_ip,
      }),
    };
  }, [dates, currentPage, pageSize, sortField, sortOrder, appliedFilters]);

  const { data, isFetching, isError, error, refetch } = useGetStaffActivityLog(
    staffId,
    logParams,
  );

  // Cache the most recent BE-provided filter catalog so the dropdowns keep
  // their options while a refetch is in flight. The BE bundles this with the
  // activity payload rather than exposing a dedicated endpoint.
  //
  // Seed as soon as `available_filters` is present at all — checking for a
  // non-empty `sections` array here would leave the Section dropdown stuck on
  // a loading spinner if BE ever returns `{ sections: [] }`, since the "All"
  // sentinel is already usable on its own.
  const [availableFilters, setAvailableFilters] =
    useState<StaffActivityLogAvailableFilters | null>(null);
  useEffect(() => {
    if (data?.available_filters != null) {
      setAvailableFilters(data.available_filters);
    }
  }, [data?.available_filters]);

  const sectionOptions = useMemo(
    () => [
      { value: SECTION_ALL, label: AppStrings.ALL },
      ...(availableFilters?.sections ?? []).map((s) => ({
        value: s.value,
        label: s.label,
      })),
    ],
    [availableFilters],
  );

  // Action type dropdown only lists options when a specific section is chosen.
  // The leading "All" option means "any action within this section".
  const actionTypeOptions = useMemo(() => {
    if (!watchedSection || watchedSection === SECTION_ALL) return [];
    const section = findSection(availableFilters, watchedSection);
    return [
      { value: ACTION_TYPE_ALL, label: AppStrings.ALL },
      ...(section?.action_types ?? []).map((a) => ({
        value: a.value,
        label: a.label,
      })),
    ];
  }, [availableFilters, watchedSection]);

  const isPanelDirty =
    (watchedSection ?? SECTION_ALL) !== appliedFilters.section ||
    (watchedActionType ?? ACTION_TYPE_ALL) !== appliedFilters.action_type ||
    watchedSourceIp !== appliedFilters.source_ip;

  // "Clear" is only a meaningful action when either the applied state or the
  // draft state deviates from the ALL/ALL default; otherwise clicking it is a
  // no-op, so we keep it visually neutral.
  const isClearActive =
    appliedFilters.section !== DEFAULT_FILTERS.section ||
    appliedFilters.action_type !== DEFAULT_FILTERS.action_type ||
    appliedFilters.source_ip !== DEFAULT_FILTERS.source_ip ||
    (watchedSection != null && watchedSection !== DEFAULT_FILTERS.section) ||
    (watchedActionType != null &&
      watchedActionType !== DEFAULT_FILTERS.action_type) ||
    watchedSourceIp !== DEFAULT_FILTERS.source_ip;

  const tableRows = useMemo(() => {
    const items = data?.items ?? [];
    return items.map((item, i) => mapActivityApiItemToRow(item, i));
  }, [data]);

  const totalActions = data?.total_count ?? tableRows.length;

  useEffect(() => {
    setCurrentPage(1);
  }, [staffId, logParams.start_date, logParams.end_date]);

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ActivityRow> | SorterResult<ActivityRow>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s?.field) {
      setSortField(String(s.field));
      setSortOrder(
        s.order === "ascend" || s.order === "descend" ? s.order : null,
      );
      setCurrentPage(1);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const handleClearFilters = () => {
    form.setFieldsValue(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    const values = form.getFieldsValue();
    const sourceIp = (values.source_ip ?? "").trim();
    form.setFieldValue("source_ip", sourceIp);
    setAppliedFilters({
      section: values.section ?? SECTION_ALL,
      action_type: values.action_type ?? ACTION_TYPE_ALL,
      source_ip: sourceIp,
    });
    setCurrentPage(1);
  };

  // Cascade: switching sections invalidates the currently selected action_type
  // (it may not exist in the new section), so we snap the draft value back to
  // "All" — the neutral "any action within this section" state.
  const handleSectionChange = () => {
    form.setFieldsValue({ action_type: ACTION_TYPE_ALL });
  };

  const columns = [
    {
      title: AppStrings.DATE_TIME,
      dataIndex: "dateTime" as const,
      key: "dateTime",
      showSorterTooltip: false,
      width: 200,
    },
    {
      title: AppStrings.SECTION,
      dataIndex: "section" as const,
      key: "section",
      showSorterTooltip: false,
      width: 140,
      render: (text: string) => text || "-",
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username" as const,
      key: "username",
      showSorterTooltip: false,
      width: 200,
      render: (text: string, record: ActivityRow) => {
        if (
          !record.username ||
          record.userId === staffId ||
          !verifyUserId(record.userId)
        ) {
          return <span>-</span>;
        }
        const userId = record.userId;
        const href = `${NAVIGATION_ROUTES.USER_MANAGER}?userId=${encodeURIComponent(userId)}`;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="activity-tab-username-cell activity-tab-username-link"
          >
            <UserAvatar />
            <span className="activity-tab-username-text">{text}</span>
          </a>
        );
      },
    },
    {
      title: AppStrings.ACTION_TYPE,
      dataIndex: "actionType" as const,
      key: "actionType",
      showSorterTooltip: false,
      width: 200,
    },
    {
      title: <AiColumnTitle />,
      dataIndex: "section" as const,
      key: "ai",
      width: 100,
      render: (value: string) => <AiActionValue eventCategory={value} />,
    },
    {
      title: AppStrings.PAM_STAFF_IP_ADDRESS,
      dataIndex: "ipAddress" as const,
      key: "ipAddress",
      width: 160,
      render: (value: string) => value || "—",
    },
    {
      title: AppStrings.REASON,
      dataIndex: "reason" as const,
      key: "reason",
      width: 200,
      render: (text: string) => {
        if (!text) return "-";
        return text;
      },
    },
  ];

  return (
    <div className="activity-tab-container">
      <div className="activity-tab-table-container">
        <div className="activity-tab-header-container">
          <div className="activity-tab-header-text-container">
            <p className="activity-tab-header-text">
              {AppStrings.ACTIVITY_HISTORY}
            </p>
          </div>
          <div className="activity-tab-header-actions">
            <CustomDatePicker
              getData={() => {
                void refetch();
              }}
              dates={dates}
              setDates={setDates}
              showPresets={true}
              startRange={DATE_RANGE.LAST_30_DAYS}
            />
            <button
              type="button"
              className="activity-tab-filter-toggle"
              data-testid="activity-tab-filter-toggle"
              aria-label={AppStrings.FILTERS_TOGGLE_ARIA}
              aria-expanded={showFilters}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <Filter size={18} />
              {showFilters ?
                <ChevronUp size={16} />
              : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
        {showFilters ?
          <>
            {/* Re-seed the form from the currently applied state on every open
                so the draft never diverges silently after a toggle. */}
            <Form form={form} initialValues={appliedFilters}>
              <div className="activity-tab-selectors">
                <div className="activity-tab-selector">
                  <span>{AppStrings.SECTION}</span>
                  <Form.Item name="section" noStyle>
                    <Select
                      options={sectionOptions}
                      loading={!availableFilters}
                      onChange={handleSectionChange}
                    />
                  </Form.Item>
                </div>
                <div className="activity-tab-selector">
                  <span>{AppStrings.ACTION_TYPE}</span>
                  <Form.Item name="action_type" noStyle>
                    <Select
                      options={actionTypeOptions}
                      disabled={
                        !watchedSection || watchedSection === SECTION_ALL
                      }
                    />
                  </Form.Item>
                </div>
                <div className="activity-tab-selector">
                  <span>{AppStrings.PAM_STAFF_IP_ADDRESS}</span>
                  <Form.Item name="source_ip" noStyle>
                    <Input
                      placeholder={AppStrings.PAM_STAFF_IP_ADDRESS_PLACEHOLDER}
                      className="activity-tab-filter-input"
                    />
                  </Form.Item>
                </div>
              </div>
            </Form>
            <div className="activity-tab-selector-actions">
              <button
                type="button"
                className={
                  isClearActive ?
                    "activity-tab-clear activity-tab-clear-active"
                  : "activity-tab-clear"
                }
                onClick={handleClearFilters}
              >
                {AppStrings.CLEAR_FILTERS}
              </button>
              <button
                type="button"
                className={
                  isPanelDirty ?
                    "activity-tab-apply activity-tab-apply-dirty"
                  : "activity-tab-apply"
                }
                onClick={handleApplyFilters}
              >
                {AppStrings.APPLY}
              </button>
            </div>
          </>
        : null}
        {isError ?
          <Alert
            type="error"
            showIcon
            message={
              (error && (error as Error).message) ||
              "Unable to load activity log. Please try again."
            }
            style={{ marginBottom: 12 }}
          />
        : null}
        <TypedDataTable
          columns={columns}
          dataSource={tableRows}
          pagination={false}
          onChange={handleTableChange}
          rowKey="key"
          scroll={{ x: 1460, y: 500 }}
          loading={isFetching}
        />
        <div className="activity-tab-pagination-container">
          <div className="activity-tab-pagination-info">
            <Select
              value={pageSize}
              onChange={(value) => {
                setCurrentPage(1);
                setPageSize(value);
              }}
              style={{ width: 75 }}
            >
              <Select.Option value={10}>10</Select.Option>
              <Select.Option value={20}>20</Select.Option>
              <Select.Option value={50}>50</Select.Option>
              <Select.Option value={100}>100</Select.Option>
            </Select>
            <span>per page of {totalActions} actions</span>
          </div>
          <Pagination
            current={currentPage}
            total={totalActions}
            pageSize={pageSize}
            showSizeChanger={false}
            onChange={(page) => setCurrentPage(page)}
            itemRender={(page, type, originalElement) => {
              if (type === "page") {
                return (
                  <span className={page === currentPage ? "active" : ""}>
                    {page}
                  </span>
                );
              }
              if (type === "prev") {
                return <span>&lt; Back</span>;
              }
              if (type === "next") {
                return <span>Next &gt;</span>;
              }
              return originalElement;
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default ActivityTab;
